/**
 * Admin Teams - API Schemas
 *
 * Admin endpoints for managing published Teams apps across all organizations.
 * Protected by PBAC admin:teams permission.
 *
 * @module api/domains/admin/zadmin-teams
 */

import { z } from "zod";

import { ApiSuccessSchema } from "../../common";
import type { InferSchemas } from "../../schema-inference";

// ============================================================
// RESPONSE SCHEMAS
// ============================================================

/**
 * Teams app entry in list
 */
const TeamsAppListItemSchema = z.object({
  /** ApiKeyConnection ID */
  connectionId: z.string(),
  /** Organization ID */
  organizationId: z.string(),
  /** Organization name */
  organizationName: z.string(),
  /** App name from connection */
  appName: z.string(),
  /** Stable manifest ID (UUID, null if unpublished) */
  manifestId: z.string().nullable(),
  /** Current manifest version (e.g., "1.0.0", null if unpublished) */
  manifestVersion: z.string().nullable(),
  /** MS Graph app catalog ID (null if unpublished) */
  teamsAppId: z.string().nullable(),
  /** Webhook URL (null if not configured) */
  webhookUrl: z.string().nullable(),
  /** When published to Teams (ISO date, null if unpublished) */
  publishedAt: z.string().nullable(),
  /** Connection created at (ISO date) */
  createdAt: z.string(),
  /** Whether connection has no active deployments */
  isOrphaned: z.boolean(),
  /** Whether app has been published to Teams catalog */
  isPublished: z.boolean(),
  /** Number of deployments using this connection */
  deploymentsCount: z.number()
});

/**
 * List response with apps
 */
const ListTeamsAppsResponseSchema = z.object({
  apps: z.array(TeamsAppListItemSchema),
  total: z.number()
});

/**
 * Delete response with result
 */
const DeleteTeamsAppResponseSchema = z.object({
  success: z.boolean(),
  message: z.string()
});

/**
 * Delete from Azure response
 */
const DeleteFromAzureResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  deletedFromAzure: z.boolean(),
  deletedFromDatabase: z.boolean()
});

/**
 * Azure catalog app reconciliation status
 */
export const AzureReconciliationStatus = z.enum(["synced", "azure_only", "db_only"]);

/**
 * Teams app from Azure with reconciliation status
 */
const TeamsAppFromAzureSchema = z.object({
  /** MS Graph app ID (teamsAppId) */
  appId: z.string(),
  /** Manifest ID (externalId in Azure, stable across versions, null for built-in apps) */
  manifestId: z.string().nullable(),
  /** App display name */
  displayName: z.string(),
  /** Current version */
  version: z.string(),
  /** Organization name (if found in DB) */
  organizationName: z.string().nullable(),
  /** ApiKeyConnection ID (if found in DB) */
  connectionId: z.string().nullable(),
  /** Reconciliation status */
  status: AzureReconciliationStatus,
  /** Whether app can be deleted from Azure */
  canDelete: z.boolean(),
  /** Webhook URL (if synced with DB) */
  webhookUrl: z.string().nullable(),
  /** Published timestamp (if synced with DB) */
  publishedAt: z.string().nullable(),
  /** Number of deployments using this connection */
  deploymentsCount: z.number(),
  /** Connection created timestamp (if synced with DB) */
  createdAt: z.string().nullable(),
  /** Bot display name from DB settings (if synced) */
  botDisplayName: z.string().nullable(),
  /** Deployment names using this connection (if synced) */
  deploymentNames: z.array(z.string()),
  /** Full metadata from Azure MS Graph API */
  azureMetadata: z.object({
    distributionMethod: z.string().optional(),
    description: z.string().optional(),
    shortDescription: z.string().optional(),
    lastModifiedDateTime: z.string().optional(),
    createdDateTime: z.string().optional(),
    publishingState: z.string().optional()
  })
});

/**
 * List Azure apps response with reconciliation stats
 */
const ListAzureAppsResponseSchema = z.object({
  apps: z.array(TeamsAppFromAzureSchema),
  total: z.number(),
  synced: z.number(),
  azureOnly: z.number(),
  dbOnly: z.number()
});

// ============================================================
// ADMIN TEAMS SCHEMAS
// ============================================================

/**
 * Zod schemas for Admin Teams endpoints
 *
 * All endpoints require admin:teams permission.
 */
export const ZAdminTeams = {
  /**
   * List all published Teams apps
   *
   * GET /api/admin/teams/apps
   *
   * Permissions: admin:teams (read)
   */
  ListApps: {
    method: "GET" as const,
    path: "/api/admin/teams/apps",
    Response: ApiSuccessSchema(ListTeamsAppsResponseSchema)
  },

  /**
   * List Teams apps from Azure Graph API (source of truth)
   *
   * GET /api/admin/teams/azure-apps
   *
   * Permissions: admin:teams (read)
   *
   * Queries MS Graph /appCatalogs/teamsApps and cross-references with database
   * to show reconciliation status (synced, azure_only, db_only).
   */
  ListAzureApps: {
    method: "GET" as const,
    path: "/api/admin/teams/azure-apps",
    Response: ApiSuccessSchema(ListAzureAppsResponseSchema)
  },

  /**
   * Delete Teams app from catalog and database
   *
   * DELETE /api/admin/teams/apps/:appId
   *
   * Permissions: admin:teams (delete)
   *
   * Deletes:
   * - App from MS Graph Teams catalog
   * - ApiKeyConnection record
   */
  DeleteApp: {
    method: "DELETE" as const,
    path: "/api/admin/teams/connections/:connectionId",
    PathVars: z.object({
      /** ApiKeyConnection ID */
      connectionId: z.string().uuid()
    }),
    Response: ApiSuccessSchema(DeleteTeamsAppResponseSchema)
  },

  /**
   * Delete Teams app from Azure catalog (and database if matched)
   *
   * DELETE /api/admin/teams/azure-apps/:appId/:manifestId
   *
   * Permissions: admin:teams (delete)
   *
   * Deletes:
   * - App from MS Graph /appCatalogs/teamsApps/{appId}
   * - Matching ApiKeyConnection from database (if manifestId matches)
   */
  DeleteFromAzure: {
    method: "DELETE" as const,
    path: "/api/admin/teams/azure-apps/:appId/:manifestId",
    PathVars: z.object({
      /** MS Graph app ID */
      appId: z.string(),
      /** Manifest ID for database matching (use 'none' if not available) */
      manifestId: z.string()
    }),
    Response: ApiSuccessSchema(DeleteFromAzureResponseSchema)
  }
} as const;

export type TApiAdminTeams = InferSchemas<typeof ZAdminTeams>;
